import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { messagesFr, MessageKey } from './messages-fr';
import { messagesEn } from './messages-en';

type Lang = 'fr' | 'en';
type Messages = Record<MessageKey, string>;

const MESSAGES: Record<Lang, Messages> = { fr: messagesFr, en: messagesEn };

interface I18nContextValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: MessageKey, params?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    if (typeof window === 'undefined') return 'fr';
    const stored = window.localStorage.getItem('gorampe-lang');
    return stored === 'en' ? 'en' : 'fr';
  });

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const setLang = (l: Lang) => {
    setLangState(l);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('gorampe-lang', l);
      document.documentElement.lang = l;
    }
  };

  const t = (key: MessageKey, params?: Record<string, string | number>): string => {
    let msg: string = MESSAGES[lang][key] ?? String(key);
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        msg = msg.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
      }
    }
    return msg;
  };

  return <I18nContext.Provider value={{ lang, setLang, t }}>{children}</I18nContext.Provider>;
}

export function useTranslation(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useTranslation must be used inside <I18nProvider>');
  return ctx;
}

export type { Lang, MessageKey };
